import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { StarIcon } from "@/components/icons/StarIcon";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { Trash2, Pencil, X } from "lucide-react";
import { submitReview, deleteMyReview } from "@/lib/reviews.functions";
import { useLanguage } from "@/i18n/LanguageContext";

type Review = {
  id: string;
  rating: number;
  comment: string;
  created_at: string;
  featured?: boolean;
  display_name: string | null;
  avatar_url: string | null;
  is_mine?: boolean;
};

type SortMode = "newest" | "oldest" | "highest" | "lowest";

const COPY = {
  en: {
    sortNewest: "newest", sortOldest: "oldest", sortHighest: "highest rated", sortLowest: "lowest rated",
    shortTop: "top", shortLow: "low",
    yourReview: "your review", editYourReview: "edit your review", writeReview: "write a review",
    edit: "edit", del: "delete", cancel: "cancel", save: "save changes", post: "post review", saving: "saving…",
    tapStar: "tap a star", placeholder: "share what you loved — the dishes, the vibe, the service…",
    signIn: "sign in", toWrite: "to write a review.",
    reviewsLabel: (n: number) => `${n} review${n === 1 ? "" : "s"}`,
    shown: "shown", clear: "clear",
    noneYet: "no reviews yet — be the first.", noneStar: (s: number) => `no ${s}-star reviews yet.`,
    pending: "Thanks! Your review is waiting for approval and will appear once a moderator has checked it.",
    rejected: "Your review was not approved. You can edit it and submit it again.",
    posted: "review sent for approval", updated: "review updated — waiting for approval",
    removed: "review removed", confirmDelete: "delete your review?",
    pickStar: "pick a star rating first", failSave: "failed to save review", failDelete: "failed to delete review",
    anonymous: "anonymous", featured: "featured", you: "you", filterBy: (s: number) => `showing ${s}-star reviews`,
  },
  fi: {
    sortNewest: "uusimmat", sortOldest: "vanhimmat", sortHighest: "parhaat", sortLowest: "heikoimmat",
    shortTop: "parhaat", shortLow: "heikoimmat",
    yourReview: "arviosi", editYourReview: "muokkaa arviotasi", writeReview: "kirjoita arvio",
    edit: "muokkaa", del: "poista", cancel: "peruuta", save: "tallenna", post: "lähetä arvio", saving: "tallennetaan…",
    tapStar: "valitse tähdet", placeholder: "kerro mistä pidit — ruoat, tunnelma, palvelu…",
    signIn: "kirjaudu sisään", toWrite: "kirjoittaaksesi arvion.",
    reviewsLabel: (n: number) => `${n} arvio${n === 1 ? "" : "ta"}`,
    shown: "näytetään", clear: "tyhjennä",
    noneYet: "ei vielä arvioita — ole ensimmäinen.", noneStar: (s: number) => `ei vielä ${s} tähden arvioita.`,
    pending: "Kiitos! Arviosi odottaa hyväksyntää ja julkaistaan tarkistuksen jälkeen.",
    rejected: "Arviotasi ei hyväksytty. Voit muokata sitä ja lähettää uudelleen.",
    posted: "arvio lähetetty hyväksyttäväksi", updated: "arvio päivitetty — odottaa hyväksyntää",
    removed: "arvio poistettu", confirmDelete: "poistetaanko arviosi?",
    pickStar: "valitse ensin tähdet", failSave: "arvion tallennus epäonnistui", failDelete: "arvion poisto epäonnistui",
    anonymous: "nimetön", featured: "nostettu", you: "sinä", filterBy: (s: number) => `näytetään ${s} tähden arviot`,
  },
} as const;

export function PlaceReviews({ placeSlug, autoFocusComposer = false }: { placeSlug: string; autoFocusComposer?: boolean }) {
  const { lang } = useLanguage();
  const c = lang === "fi" ? COPY.fi : COPY.en;
  const SORTS: { id: SortMode; label: string; shortLabel: string }[] = [
    { id: "newest", label: c.sortNewest, shortLabel: c.sortNewest },
    { id: "oldest", label: c.sortOldest, shortLabel: c.sortOldest },
    { id: "highest", label: c.sortHighest, shortLabel: c.shortTop },
    { id: "lowest", label: c.sortLowest, shortLabel: c.shortLow },
  ];
  const [myStatus, setMyStatus] = useState<string | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sort, setSort] = useState<SortMode>("newest");
  const [filterStar, setFilterStar] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    const { data: rows, error } = await supabase
      .from("place_reviews_public" as any)
      .select("id, rating, comment, created_at, featured, display_name, avatar_url")
      .eq("place_slug", placeSlug)
      .order("created_at", { ascending: false });
    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }
    const list = ((rows ?? []) as any[]) as Review[];

    // Separately fetch the current user's own review (if any) so we can
    // detect "mine" without exposing user_ids of other reviewers.
    let mineId: string | null = null;
    let ownRow: any = null;
    const { data: auth } = await supabase.auth.getUser();
    if (auth.user) {
      const { data: own } = await supabase
        .from("place_reviews" as any)
        .select("id, rating, comment, created_at, status")
        .eq("place_slug", placeSlug)
        .eq("user_id", auth.user.id)
        .maybeSingle();
      ownRow = own ?? null;
      mineId = (own as any)?.id ?? null;
      setMyStatus((own as any)?.status ?? null);
    } else {
      setMyStatus(null);
    }

    const merged: Review[] = list.map((r) => ({ ...r, is_mine: mineId !== null && r.id === mineId }));
    // A pending/rejected review is filtered out of the public view, but the
    // author must still see (and be able to edit) their own submission.
    if (ownRow && !merged.some((r) => r.is_mine)) {
      merged.unshift({
        id: ownRow.id,
        rating: ownRow.rating,
        comment: ownRow.comment ?? "",
        created_at: ownRow.created_at,
        display_name: null,
        avatar_url: null,
        is_mine: true,
      } as Review);
    }

    setReviews(merged);
    setLoading(false);
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) =>
      setUserId(s?.user?.id ?? null),
    );
    load();
    return () => sub.subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeSlug]);

  const myReview = useMemo(
    () => reviews.find((r) => r.is_mine) ?? null,
    [reviews],
  );

  // Hydrate the form once we know which review is mine, unless the user is
  // actively editing or composing a new one.
  useEffect(() => {
    if (editing) return;
    if (myReview) {
      setRating(myReview.rating);
      setComment(myReview.comment);
    } else {
      setRating(0);
      setComment("");
    }
  }, [myReview?.id, editing]);

  // Auto-enter edit mode when opened via the "Write a review" CTA so the
  // composer is immediately interactive even if the user already has a review.
  useEffect(() => {
    if (autoFocusComposer && userId && myReview && !editing) {
      setEditing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocusComposer, userId, myReview?.id]);

  const submitReviewFn = useServerFn(submitReview);
  const deleteReviewFn = useServerFn(deleteMyReview);

  const submit = async () => {
    if (!userId) return;
    if (rating < 1 || rating > 5) {
      toast.error(c.pickStar);
      return;
    }
    setSubmitting(true);
    try {
      await submitReviewFn({
        data: {
          placeSlug,
          rating,
          comment: comment.trim(),
        },
      });
      toast.success(myReview ? c.updated : c.posted);
      setComment(comment.trim());
      setEditing(false);
      load();
    } catch (e: any) {
      toast.error(e?.message ?? c.failSave);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!myReview) return;
    if (!window.confirm(c.confirmDelete)) return;
    try {
      await deleteReviewFn({ data: { placeSlug } });
      setComment("");
      setRating(0);
      setEditing(false);
      toast.success(c.removed);
      load();
    } catch (e: any) {
      toast.error(e?.message ?? c.failDelete);
    }
  };

  // ---- derived ----
  // Own review is only part of the public list once it has been approved.
  const publicList = useMemo(
    () => reviews.filter((r) => !(r.is_mine && myStatus !== null && myStatus !== "approved")),
    [reviews, myStatus],
  );
  const total = publicList.length;
  const avg = total ? publicList.reduce((s, r) => s + r.rating, 0) / total : 0;
  const dist = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: publicList.filter((r) => r.rating === star).length,
  }));

  const visible = useMemo(() => {
    const arr = filterStar
      ? publicList.filter((r) => r.rating === filterStar)
      : [...publicList];
    arr.sort((a, b) => {
      // Featured reviews always pinned to the top
      if (!!b.featured !== !!a.featured) return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
      if (sort === "newest") return +new Date(b.created_at) - +new Date(a.created_at);
      if (sort === "oldest") return +new Date(a.created_at) - +new Date(b.created_at);
      if (sort === "highest") return b.rating - a.rating;
      return a.rating - b.rating;
    });
    return arr;
  }, [publicList, sort, filterStar]);

  const isComposing = !myReview || editing;

  // Approved reviews are public; pending/rejected ones stay with their author.
  const SHOW_REVIEWS_LIST = true;

  return (
    <div className="space-y-6">
      {SHOW_REVIEWS_LIST && (
      <>
      {/* SUMMARY CARD */}
      <div className="rounded-2xl border border-navy/10 bg-white/80 backdrop-blur-md shadow-[0_8px_30px_-12px_rgba(15,23,42,0.12)] p-5 sm:p-6">
        <div className="grid sm:grid-cols-[auto_1fr] gap-6 sm:gap-8 items-center">
          {/* Avg */}
          <div className="text-center sm:text-left">
            <div className="flex items-baseline gap-2 justify-center sm:justify-start">
              <span className="font-heading text-navy text-5xl tabular-nums leading-none">
                {total ? avg.toFixed(1) : "—"}
              </span>
              <span className="text-navy/55 text-sm">/ 5</span>
            </div>
            <div className="mt-2 flex gap-0.5 justify-center sm:justify-start" aria-label={`${avg.toFixed(1)} stars`}>
              {Array.from({ length: 5 }).map((_, i) => (
                <StarIcon
                  key={i}
                  className={`size-4 ${i < Math.round(avg) ? "text-[var(--star)]" : "text-navy/15"}`}
                />
              ))}
            </div>
            <p className="mt-1.5 text-navy/60 text-xs font-mono uppercase tracking-[0.18em]">
              {c.reviewsLabel(total)}
            </p>
          </div>

          {/* Distribution */}
          <div className="space-y-1.5">
            {dist.map(({ star, count }) => {
              const pct = total ? (count / total) * 100 : 0;
              const active = filterStar === star;
              return (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFilterStar(active ? null : star)}
                  disabled={count === 0}
                  className={`w-full flex items-center gap-2.5 group ${count === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                  aria-pressed={active}
                  title={count ? `filter by ${star} star` : "no reviews"}
                >
                  <span className="font-mono text-[11px] text-navy/65 w-3 text-right tabular-nums">
                    {star}
                  </span>
                  <StarIcon className="size-3 text-[var(--star)]" />
                  <div className="flex-1 h-1.5 rounded-full bg-navy/8 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${active ? "bg-teal" : "bg-navy/35 group-hover:bg-navy/55"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="font-mono text-[11px] text-navy/55 w-6 text-right tabular-nums">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {filterStar && (
          <div className="mt-4 flex items-center gap-2 text-xs text-navy/70">
            <span>{c.filterBy(filterStar)}</span>
            <button
              onClick={() => setFilterStar(null)}
              className="inline-flex items-center gap-1 rounded-full border border-navy/15 px-2 py-0.5 hover:border-navy/40"
            >
              <X className="size-3" /> {c.clear}
            </button>
          </div>
        )}
      </div>
      </>
      )}

      {/* COMPOSER */}
      {userId ? (
        <div className="rounded-2xl border border-navy/10 bg-white p-4 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <p className="font-heading text-navy text-base">
              {myReview && !editing ? c.yourReview : myReview ? c.editYourReview : c.writeReview}
            </p>
            {myReview && !editing && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-navy hover:border-teal hover:text-teal transition-colors"
                >
                  <Pencil className="size-3" /> {c.edit}
                </button>
                <button
                  onClick={remove}
                  aria-label="delete review"
                  className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-navy hover:border-red-500 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="size-3" /> {c.del}
                </button>
              </div>
            )}
          </div>

          {myStatus && myStatus !== "approved" && (
            <div className={`mt-3 rounded-xl px-3 py-2 text-xs leading-relaxed ${myStatus === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
              {myStatus === "rejected" ? c.rejected : c.pending}
            </div>
          )}

          {/* Star picker */}
          <div className="mt-3 flex items-center gap-1" onMouseLeave={() => setHoverRating(0)}>
            {Array.from({ length: 5 }).map((_, i) => {
              const v = i + 1;
              const filled = (hoverRating || rating) >= v;
              const interactive = isComposing;
              return (
                <button
                  key={v}
                  type="button"
                  disabled={!interactive}
                  onMouseEnter={() => interactive && setHoverRating(v)}
                  onClick={() => interactive && setRating(v)}
                  aria-label={`${v} star${v > 1 ? "s" : ""}`}
                  className={`p-0.5 rounded-md transition-transform ${interactive ? "hover:scale-110" : "cursor-default"}`}
                >
                  <StarIcon
                    className={`size-7 transition-colors ${filled ? "text-[var(--star)]" : "text-navy/20"}`}
                  />
                </button>
              );
            })}
            <span className="ml-2 font-mono text-[11px] text-navy/55 tabular-nums">
              {(hoverRating || rating) > 0 ? `${hoverRating || rating} / 5` : c.tapStar}
            </span>
          </div>

          {isComposing ? (
            <>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 1000))}
                placeholder={c.placeholder}
                rows={4}
                className="mt-3 w-full rounded-xl border border-navy/15 bg-white px-3 py-2.5 text-base md:text-sm text-navy placeholder:text-navy/35 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/15 resize-none"
              />
              <div className="mt-1 flex justify-end">
                <span className="font-mono text-[10px] text-navy/45 tabular-nums">
                  {comment.length} / 1000
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={submit}
                  disabled={submitting || rating < 1}
                  className="bg-navy hover:bg-royal text-white px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest disabled:opacity-40 transition-colors"
                >
                  {submitting ? c.saving : myReview ? c.save : c.post}
                </button>
                {myReview && editing && (
                  <button
                    onClick={() => {
                      setEditing(false);
                      setRating(myReview.rating);
                      setComment(myReview.comment);
                    }}
                    className="border border-navy/20 text-navy px-4 py-2.5 rounded-full text-xs font-semibold uppercase tracking-widest hover:border-navy/50"
                  >
                    {c.cancel}
                  </button>
                )}
              </div>
            </>
          ) : (
            myReview?.comment && (
              <p className="mt-3 text-navy/85 text-sm leading-relaxed whitespace-pre-wrap">
                {myReview.comment}
              </p>
            )
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-navy/20 bg-white/50 p-4 text-sm text-navy/80">
          <Link to="/login" className="underline font-medium text-navy">{c.signIn}</Link>{" "}
          {c.toWrite}
        </div>
      )}

      {SHOW_REVIEWS_LIST && (
      <>
      {/* SORT BAR */}
      {total > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-navy/55 shrink-0">
            {visible.length}/{total} {c.shown}
          </p>
          <div className="flex items-center gap-0.5 sm:gap-1 rounded-full border border-navy/10 bg-white p-0.5 sm:p-1 shadow-sm w-full sm:w-auto">
            {SORTS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSort(s.id)}
                className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.08em] sm:tracking-[0.12em] whitespace-nowrap transition-colors ${
                  sort === s.id
                    ? "bg-navy text-white"
                    : "text-navy/65 hover:text-navy"
                }`}
              >
                <span className="sm:hidden">{s.shortLabel}</span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* LIST */}
      <ul className="space-y-3">
        {loading && (
          <>
            {Array.from({ length: 3 }).map((_, i) => (
              <li
                key={i}
                className="rounded-2xl border border-navy/10 bg-white p-4 animate-pulse"
              >
                <div className="flex items-center gap-3">
                  <div className="size-9 rounded-full bg-navy/10" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-24 rounded bg-navy/10" />
                    <div className="h-2 w-16 rounded bg-navy/10" />
                  </div>
                </div>
                <div className="mt-3 h-3 w-full rounded bg-navy/10" />
                <div className="mt-2 h-3 w-2/3 rounded bg-navy/10" />
              </li>
            ))}
          </>
        )}
        {!loading && visible.length === 0 && (
          <li className="rounded-2xl border border-dashed border-navy/15 bg-white/50 p-6 text-center text-navy/60 text-sm">
            {filterStar ? c.noneStar(filterStar) : c.noneYet}
          </li>
        )}
        {visible.map((r) => {
          const mine = !!r.is_mine;
          return (
            <li
              key={r.id}
              className={`rounded-2xl border bg-white p-4 transition-colors ${
                mine ? "border-teal/40 bg-teal/[0.03]" : "border-navy/10 hover:border-navy/20"
              }`}
            >
              <div className="flex items-center gap-3">
                {r.avatar_url ? (
                  <img
                    src={r.avatar_url}
                    alt=""
                    className="size-9 rounded-full object-cover ring-1 ring-navy/10"
                  />
                ) : (
                  <div className="size-9 rounded-full bg-teal/30 text-navy grid place-items-center font-heading text-sm">
                    {(r.display_name ?? "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-navy text-sm truncate flex items-center gap-2 flex-wrap">
                    {r.display_name ?? c.anonymous}
                    {r.featured && (
                      <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.18em]">
                        {c.featured}
                      </span>
                    )}
                    {mine && (
                      <span className="rounded-full bg-teal/15 text-teal px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.18em]">
                        {c.you}
                      </span>
                    )}
                  </p>
                  <p className="text-navy/50 text-xs">
                    {new Date(r.created_at).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                </div>
                <div className="flex gap-0.5" aria-label={`${r.rating} stars`}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <StarIcon
                      key={i}
                      className={`size-4 ${i < r.rating ? "text-[var(--star)]" : "text-navy/15"}`}
                    />
                  ))}
                </div>
              </div>
              {r.comment && (
                <p className="mt-3 text-navy/85 text-sm leading-relaxed whitespace-pre-wrap">
                  {r.comment}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      </>
      )}
    </div>
  );
}
